import { Link } from "react-router";
import { Users } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { paths } from "@shared-core/routing/routes";

/**
 * Billing-group rollup for the customer-360 page.
 *
 * Renders nothing for a solo account (no ``group``). Otherwise shows the
 * group head and the sibling LIVE accounts, each linking to its own
 * customer-360. The group derives from Tharstern's PostingAccount — see
 * docs/features/customer-grouping/.
 */
export function CustomerGroupCard({
  group,
  currentCustomerId,
}: {
  group: API.CustomerGroup | null | undefined;
  currentCustomerId?: string;
}) {
  if (!group) return null;

  return (
    <section
      aria-label="Billing group"
      className="rounded-lg border border-border bg-muted/30 p-4 space-y-3"
    >
      <div className="flex items-center gap-2 text-sm">
        <Users className="size-4 text-muted-foreground" aria-hidden />
        <span>
          Part of{" "}
          {group.head_customer_id ? (
            <Link
              to={paths.customerDetail({ customerId: group.head_customer_id })}
              className="font-medium hover:underline"
            >
              {group.head_name || group.head_code}
            </Link>
          ) : (
            <span className="font-medium">
              {group.head_name || group.head_code}
            </span>
          )}{" "}
          <span className="text-muted-foreground">({group.head_code})</span>
          {" · "}
          {group.member_count} accounts
        </span>
      </div>

      <ul className="divide-y divide-border rounded-md border border-border bg-background">
        {(group.members ?? []).map((m) => {
          const isCurrent = m.customer_id === currentCustomerId;
          return (
            <li key={m.customer_id}>
              <Link
                to={paths.customerDetail({ customerId: m.customer_id })}
                aria-current={isCurrent ? "page" : undefined}
                className={`flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 ${
                  isCurrent ? "bg-muted/40" : ""
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-xs text-muted-foreground shrink-0">
                    {m.code}
                  </span>
                  <span className="truncate">{m.name}</span>
                </span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                  {m.is_head && (
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-secondary-foreground">
                      head
                    </span>
                  )}
                  {isCurrent && <span>you</span>}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
